import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  alpha,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PaletteIcon from '@mui/icons-material/Palette';
import PortalTemplatePickerGrid from './PortalTemplatePickerGrid';
import { LOGIN_PORTAL_THEMES } from '../../theme/loginPortalThemes';
import {
  getSharedPageAtmosphere,
  setSharedPageAtmosphere,
  subscribeSharedPageAtmosphere
} from '../../utils/sharedPageAtmosphere';

const TAB_W = 42;
const EXPANDED_PANEL_W = 420;
const LS_PINNED = 'erp4c-template-chooser-pinned';

function readPinned() {
  try {
    const v = localStorage.getItem(LS_PINNED);
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

function ThemeDetailTable({ themeId }) {
  const th = LOGIN_PORTAL_THEMES[themeId];
  if (!th) return null;
  const rows = [
    { label: 'Page gradient', value: th.background },
    { label: 'Panel background', value: th.panelBg },
    { label: 'Card surface', value: th.cardBg },
    { label: 'Primary text', value: th.textPrimary }
  ];
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: alpha('#000', 0.04),
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Stack spacing={0.6}>
        {rows.map((row) => (
          <Stack key={row.label} direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 26,
                height: 18,
                borderRadius: 0.5,
                flexShrink: 0,
                background: row.value,
                border: '1px solid',
                borderColor: alpha('#000', 0.12)
              }}
            />
            <Typography variant="caption" sx={{ minWidth: 88, fontWeight: 600, color: 'text.secondary' }}>
              {row.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'text.primary', wordBreak: 'break-all' }}
            >
              {row.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Shared Template Chooser used on Sign In, Register, Browsing, and Landing.
 * Page atmosphere selection syncs across all of those pages.
 */
export default function SharedTemplateChooser({
  title = 'Template chooser',
  subtitle = 'Same look across Landing, Sign In, Register, and Browsing.',
  children,
  topOffset
}) {
  const [pinned, setPinned] = useState(() => readPinned());
  const [expanded, setExpanded] = useState(() => readPinned());
  const [pageTemplateId, setPageTemplateId] = useState(() => getSharedPageAtmosphere());

  useEffect(() => subscribeSharedPageAtmosphere(setPageTemplateId), []);

  useEffect(() => {
    if (pinned) setExpanded(true);
  }, [pinned]);

  const togglePin = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_PINNED, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      if (next) setExpanded(true);
      return next;
    });
  }, []);

  const toggleExpanded = () => {
    if (pinned) return;
    setExpanded((v) => !v);
  };

  const onSelectAtmosphere = (id) => {
    const v = setSharedPageAtmosphere(id);
    setPageTemplateId(v);
  };

  const showPanel = expanded || pinned;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: topOffset || { xs: 16, sm: 20 },
        zIndex: 1300,
        display: 'flex',
        alignItems: 'flex-start',
        maxWidth: '100vw',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' }
      }}
    >
      <Tooltip title={pinned ? 'Unpin first to collapse' : showPanel ? 'Collapse template chooser' : 'Expand template chooser'}>
        <span>
          <Box
            component="button"
            type="button"
            onClick={toggleExpanded}
            disabled={pinned}
            aria-expanded={showPanel}
            aria-label={showPanel ? 'Collapse template chooser' : 'Expand template chooser'}
            sx={{
              width: TAB_W,
              flexShrink: 0,
              border: 'none',
              cursor: pinned ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 1.25,
              px: 0.35,
              bgcolor: pinned ? '#b91c1c' : 'primary.main',
              color: '#fff',
              opacity: pinned ? 0.95 : 1,
              borderRadius: '0 10px 10px 0',
              boxShadow: 3,
              transition: 'background-color 0.15s ease, transform 0.15s ease',
              '&:hover': {
                bgcolor: '#dc2626',
                transform: pinned ? 'none' : 'translateX(2px)'
              },
              '&:disabled': { color: '#fff', opacity: 0.95 }
            }}
          >
            {showPanel ? <ChevronLeftIcon sx={{ fontSize: 20 }} /> : <ChevronRightIcon sx={{ fontSize: 20 }} />}
            <Typography
              variant="caption"
              sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                fontWeight: 800,
                letterSpacing: 0.14,
                fontSize: '0.68rem',
                lineHeight: 1.15,
                py: 0.5,
                userSelect: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              TEMPLATE CHOOSER
            </Typography>
          </Box>
        </span>
      </Tooltip>

      {showPanel ? (
        <Paper
          elevation={10}
          sx={{
            width: EXPANDED_PANEL_W,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'min(80vh, 760px)',
            overflow: 'auto',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha('#fff', 0.98)
          }}
        >
          <Box
            sx={{
              p: 1.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {subtitle}
                {pinned ? ' Panel is pinned open.' : ''}
              </Typography>
            </Box>
            <Tooltip title={pinned ? 'Unpin (allows collapse)' : 'Pin open (locks expand)'}>
              <IconButton
                size="small"
                onClick={togglePin}
                color={pinned ? 'error' : 'default'}
                aria-label={pinned ? 'Unpin template panel' : 'Pin template panel open'}
                aria-pressed={pinned}
              >
                {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ p: 1.75 }}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px !important',
                mb: children ? 1.5 : 0,
                '&:before': { display: 'none' },
                overflow: 'hidden',
                bgcolor: 'background.paper'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 52 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 0.5, minWidth: 0, width: '100%' }}>
                  <Box sx={{ color: 'error.main', display: 'flex', flexShrink: 0 }}>
                    <PaletteIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                      Page atmosphere
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      Shared across Sign In, Register, Browsing & Landing
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={LOGIN_PORTAL_THEMES[pageTemplateId]?.label || pageTemplateId}
                    sx={{ fontWeight: 600, height: 22, flexShrink: 0 }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <PortalTemplatePickerGrid
                  selectedId={pageTemplateId}
                  onSelect={onSelectAtmosphere}
                  showDetails
                  columns="auto"
                />
                <ThemeDetailTable themeId={pageTemplateId} />
              </AccordionDetails>
            </Accordion>

            {children}
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}

export { TAB_W, EXPANDED_PANEL_W };
